import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { MaintenanceSection } from "@/components/layout/maintenance";
import { SubmitButton } from "@/components/common/Button/SubmitButton";
import { Button } from "@/components/shadcn/button";
import { ArrowLeft } from "lucide-react";
import { FormHeader } from "@/components/specific/pipeline/FormHeader";
import { FormSingleColumn } from "@/components/specific/pipeline/FormSingleColumn";
import { Input } from "@/components/common/Input";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { Select } from "@/components/common/Select";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { AddressField, EmailField, UserDefinedItemField } from "@/components/common/FormFields";
import { fetchContactFormOptions, createContact } from "@/api/contact/api";
import { searchContactUdLookup } from "@/api/autocomplete/api";
import { toSelectOptions } from "@/utils/fieldValidation";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { useReturnTo } from "@/hooks";
import { collectUserDefinedValues } from "@/utils/reactHookForm";
import type { QuickCreateContactOptions, ContactFieldOptions } from "@/types/domain";

type FormValues = {
  firstName: string;
  surname: string;
  title: string;
  preferredName: string;
  salutation: string;
  jobDescription: string;
  department: string;
  email: string;
  telephoneOffice: string;
  telephoneHome: string;
  telephoneMobile: string;
  fax: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  country: string;
  postcode: string;
  comments: string;
  primaryContact: string;
  [key: string]: string | boolean;
};

const mandatory = (label: string) => (
  <>{label} <span className="text-red-500">*</span></>
);

const fieldRules = (opts: ContactFieldOptions | undefined, label: string) =>
  opts?.isMandatory ? { required: `${label} is required` } : {};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-[15px] font-semibold text-gray-600 mb-3 pb-2 border-b border-gray-200">{children}</h3>
);

export const AddContactPage = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { goBack, withReturnTo } = useReturnTo();
  const [options, setOptions] = useState<QuickCreateContactOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register, handleSubmit, setValue, reset, control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: "", surname: "", title: "", preferredName: "", salutation: "",
      jobDescription: "", department: "", email: "",
      telephoneOffice: "", telephoneHome: "", telephoneMobile: "", fax: "",
      address1: "", address2: "", address3: "", address4: "",
      town: "", county: "", country: "", postcode: "",
      comments: "", primaryContact: "0",
    },
  });

  useEffect(() => {
    if (!companyId) return;
    setIsLoadingOptions(true);
    fetchContactFormOptions(companyId)
      .then((data) => { setOptions(data); reset(); })
      .catch(() => showErrorToast("Could not load contact form options."))
      .finally(() => setIsLoadingOptions(false));
  }, [companyId]);

  const copyCompanyAddress = () => {
    if (!options) return;
    setValue("address1", options.companyAddress1 ?? "");
    setValue("address2", options.companyAddress2 ?? "");
    setValue("address3", options.companyAddress3 ?? "");
    setValue("address4", options.companyAddress4 ?? "");
    setValue("town", options.companyTown ?? "");
    setValue("county", options.companyCounty ?? "");
    setValue("country", options.companyCountry ?? "");
    setValue("postcode", options.companyPostcode ?? "");
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!companyId) return;
    setServerError(null);
    try {
      const udValues = collectUserDefinedValues(options?.userDefinedGroups, values);
      const result = await createContact({
        customerId: companyId,
        firstName: values.firstName,
        surname: values.surname,
        title: values.title || undefined,
        preferredName: values.preferredName || undefined,
        salutation: values.salutation || undefined,
        jobDescription: values.jobDescription || undefined,
        department: values.department || undefined,
        email: values.email || undefined,
        telephoneOffice: values.telephoneOffice || undefined,
        telephoneHome: values.telephoneHome || undefined,
        telephoneMobile: values.telephoneMobile || undefined,
        fax: values.fax || undefined,
        address1: values.address1 || undefined,
        address2: values.address2 || undefined,
        address3: values.address3 || undefined,
        address4: values.address4 || undefined,
        town: values.town || undefined,
        county: values.county || undefined,
        country: values.country || undefined,
        postcode: values.postcode || undefined,
        comments: values.comments || undefined,
        primaryContact: parseInt(values.primaryContact, 10) || 0,
        userDefinedValues: udValues.length > 0 ? udValues : undefined,
      });

      if (result.success && result.contactId) {
        showSuccessToast("Contact created.");
        // replace so back-button skips the add form and returns to the originating page
        navigate(withReturnTo(`/contact/${result.contactId}`), { replace: true });
      } else {
        setServerError(result.errorMessage ?? "Failed to create contact.");
      }
    } catch {
      setServerError("An unexpected error occurred.");
    }
  });

  const hasCompanyAddress = !!(
    options?.companyAddress1 || options?.companyTown || options?.companyPostcode
  );

  return (
    <MaintenanceSection>
        <FormSingleColumn onSubmit={onSubmit}>
          <div className="max-w-4xl grid">
            <FormHeader title="Add Contact">
              <SubmitButton text={isSubmitting ? "Saving..." : "Create Contact +"} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
              <Button variant="outline" size="sm" type="button" className="text-kPurple-500" onClick={goBack}>
                <ArrowLeft size={14} />
                Cancel
              </Button>
            </FormHeader>
          </div>

          {isLoadingOptions && <LoadingPage message="Loading…" className="min-h-0 py-16 max-w-4xl" />}

          {!isLoadingOptions && options && (<>
            {serverError && <p className="text-sm text-red-600 mb-4">{serverError}</p>}

            {/* Personal + Contact fields */}
            <div className="space-y-6 max-w-4xl">
              <div>
                <SectionHeading>Personal</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {/* First Name — always required */}
                  <div>
                    <Label forField="fn-firstName" label={mandatory("First Name")} />
                    <Input id="fn-firstName" type="text" maxLength={50} inErrorState={!!errors.firstName}
                      {...register("firstName", { required: "First name is required", maxLength: { value: 50, message: "Max 50 characters" } })}
                    />
                    <ErrorLabel error={errors.firstName?.message} />
                  </div>

                  {/* Surname — always required */}
                  <div>
                    <Label forField="fn-surname" label={mandatory("Surname")} />
                    <Input id="fn-surname" type="text" maxLength={50} inErrorState={!!errors.surname}
                      {...register("surname", { required: "Surname is required", maxLength: { value: 50, message: "Max 50 characters" } })}
                    />
                    <ErrorLabel error={errors.surname?.message} />
                  </div>

                  <div>
                    <Label forField="fn-title" label="Title" />
                    <Input id="fn-title" type="text" maxLength={50}
                      inErrorState={!!errors.title}
                      {...register("title", { maxLength: { value: 50, message: "Max 50 characters" } })}
                    />
                    <ErrorLabel error={errors.title?.message} />
                  </div>

                  <div>
                    <Label forField="fn-preferred" label="Preferred Name" />
                    <Input id="fn-preferred" type="text" maxLength={50} {...register("preferredName", { maxLength: { value: 50, message: "Max 50 characters" } })} />
                  </div>

                  <div>
                    <Label forField="fn-salutation" label="Salutation" />
                    <Input id="fn-salutation" type="text" maxLength={50} {...register("salutation", { maxLength: { value: 50, message: "Max 50 characters" } })} />
                  </div>
                </div>
              </div>

              <div>
                <SectionHeading>Work</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {!options.jobDescription.isReadOnly && <div>
                    <Label forField="fn-job" label={options.jobDescription.isMandatory ? mandatory("Job Description") : "Job Description"} />
                    <Input id="fn-job" type="text" maxLength={50} disabled={options.jobDescription.isReadOnly}
                      inErrorState={!!errors.jobDescription}
                      {...register("jobDescription", { ...fieldRules(options.jobDescription, "Job Description"), maxLength: { value: 50, message: "Max 50 characters" } })}
                    />
                    <ErrorLabel error={errors.jobDescription?.message} />
                  </div>}

                  {!options.department.isReadOnly && <div>
                    <Label forField="fn-dept" label={options.department.isMandatory ? mandatory("Department") : "Department"} />
                    <Input id="fn-dept" type="text" maxLength={50} disabled={options.department.isReadOnly}
                      inErrorState={!!errors.department}
                      {...register("department", { ...fieldRules(options.department, "Department"), maxLength: { value: 50, message: "Max 50 characters" } })}
                    />
                    <ErrorLabel error={errors.department?.message} />
                  </div>}

                  <div>
                    <Label forField="fn-primary" label="Primary Contact" />
                    <Select id="fn-primary" {...register("primaryContact")}>
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <SectionHeading>Contact</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {!options.email.isReadOnly && <div className="sm:col-span-2">
                    <EmailField fieldKey="email" id="fn-email" isMandatory={options.email.isMandatory} isReadOnly={options.email.isReadOnly} register={register} errors={errors} />
                  </div>}

                  {!options.telephoneOffice.isReadOnly && <div>
                    <Label forField="fn-tel-office" label={options.telephoneOffice.isMandatory ? mandatory("Telephone (Office)") : "Telephone (Office)"} />
                    <Input id="fn-tel-office" type="text" maxLength={30} disabled={options.telephoneOffice.isReadOnly}
                      inErrorState={!!errors.telephoneOffice}
                      {...register("telephoneOffice", { ...fieldRules(options.telephoneOffice, "Telephone (Office)"), maxLength: { value: 30, message: "Max 30 characters" } })}
                    />
                    <ErrorLabel error={errors.telephoneOffice?.message} />
                  </div>}

                  {!options.telephoneMobile.isReadOnly && <div>
                    <Label forField="fn-tel-mobile" label={options.telephoneMobile.isMandatory ? mandatory("Telephone (Mobile)") : "Telephone (Mobile)"} />
                    <Input id="fn-tel-mobile" type="text" maxLength={30} disabled={options.telephoneMobile.isReadOnly}
                      inErrorState={!!errors.telephoneMobile}
                      {...register("telephoneMobile", { ...fieldRules(options.telephoneMobile, "Telephone (Mobile)"), maxLength: { value: 30, message: "Max 30 characters" } })}
                    />
                    <ErrorLabel error={errors.telephoneMobile?.message} />
                  </div>}

                  {!options.telephoneHome.isReadOnly && <div>
                    <Label forField="fn-tel-home" label={options.telephoneHome.isMandatory ? mandatory("Telephone (Home)") : "Telephone (Home)"} />
                    <Input id="fn-tel-home" type="text" maxLength={30} disabled={options.telephoneHome.isReadOnly}
                      inErrorState={!!errors.telephoneHome}
                      {...register("telephoneHome", { ...fieldRules(options.telephoneHome, "Telephone (Home)"), maxLength: { value: 30, message: "Max 30 characters" } })}
                    />
                    <ErrorLabel error={errors.telephoneHome?.message} />
                  </div>}

                  {!options.fax.isReadOnly && <div>
                    <Label forField="fn-fax" label={options.fax.isMandatory ? mandatory("Fax") : "Fax"} />
                    <Input id="fn-fax" type="text" maxLength={30} disabled={options.fax.isReadOnly}
                      inErrorState={!!errors.fax}
                      {...register("fax", { ...fieldRules(options.fax, "Fax"), maxLength: { value: 30, message: "Max 30 characters" } })}
                    />
                    <ErrorLabel error={errors.fax?.message} />
                  </div>}
                </div>
              </div>

              <div>
                <SectionHeading>Other</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {!options.comments.isReadOnly && <div className="sm:col-span-2">
                    <Label forField="fn-comments" label={options.comments.isMandatory ? mandatory("Comments") : "Comments"} />
                    <Input id="fn-comments" multiline rows={3} maxLength={8000} disabled={options.comments.isReadOnly}
                      inErrorState={!!errors.comments}
                      {...register("comments", { ...fieldRules(options.comments, "Comments"), maxLength: { value: 8000, message: "Max 8000 characters" } })}
                    />
                    <ErrorLabel error={errors.comments?.message} />
                  </div>}
                </div>
              </div>
            </div>

            {/* Address */}
            {!options.address.isReadOnly && <div className="max-w-4xl">
              <SectionHeading>Address</SectionHeading>
              <AddressField
                register={register}
                errors={errors}
                isMandatory={options.address.isMandatory}
                isReadOnly={options.address.isReadOnly}
                header={
                  hasCompanyAddress ? (
                    <div className="flex items-center justify-between mb-1">
                      <Label forField="addr1" label={options.address.isMandatory ? <>{`Address`} <span className="text-red-500">*</span></> : "Address"} />
                      <button type="button" className="text-xs text-blue-600 hover:underline" onClick={copyCompanyAddress}>
                        Copy Company Address
                      </button>
                    </div>
                  ) : undefined
                }
              />
            </div>}
          {/* User Defined Fields */}
          {options.userDefinedGroups && options.userDefinedGroups.length > 0 && (
            <div className="max-w-4xl">
              <SectionHeading>User Defined Fields</SectionHeading>
              <div className="space-y-4">
                {options.userDefinedGroups.map((group) => (
                  <div key={group.id} className="rounded border border-gray-200 p-4">
                    {group.name && (
                      <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-3">{group.name}</h4>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                      {group.items.map((item) => (
                        <UserDefinedItemField key={item.id} item={item} fieldKey={`ud_${item.itemId}`} register={register} control={control} errors={errors}
                          loadOptions={item.isAutoComplete ? (q) => searchContactUdLookup(item.itemId, q).then(toSelectOptions) : undefined}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>)}

          {/* Repeat buttons at bottom */}
          {!isLoadingOptions && options && (
            <div className="inline-flex gap-2 flex-row-reverse w-full max-w-4xl mt-6">
              <SubmitButton text={isSubmitting ? "Saving..." : "Create Contact +"} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
              <Button variant="outline" size="sm" type="button" className="text-kPurple-500" onClick={goBack}>
                <ArrowLeft size={14} />
                Cancel
              </Button>
            </div>
          )}
        </FormSingleColumn>
    </MaintenanceSection>
  );
};
